import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Palette, Save, RefreshCw, Upload, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PortalCustomization = () => {
  const { user } = useAuth();
  const [customization, setCustomization] = useState({
    primaryColor: '#0A1628',
    secondaryColor: '#D4AF37',
    accentColor: '#1a2332',
    logo: '/logo-3.png',
    favicon: '/favicon.ico',
    portalName: 'ReferBridge',
    welcomeMessage: 'Welcome to ReferBridge - Connecting practices, improving patient care.',
    footerText: '© 2025 ReferBridge. All rights reserved.',
    enableNotifications: true,
    enableChat: true,
    enableRewards: true
  });

  const [previewMode, setPreviewMode] = useState(false);

  const handleSave = () => {
    localStorage.setItem('portalCustomization', JSON.stringify(customization));
    alert('Customization settings saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('Reset to default settings?')) {
      setCustomization({
        primaryColor: '#0A1628',
        secondaryColor: '#D4AF37',
        accentColor: '#1a2332',
        logo: '/logo-3.png',
        favicon: '/favicon.ico',
        portalName: 'ReferBridge',
        welcomeMessage: 'Welcome to ReferBridge - Connecting practices, improving patient care.',
        footerText: '© 2025 ReferBridge. All rights reserved.',
        enableNotifications: true,
        enableChat: true,
        enableRewards: true
      });
      alert('Settings reset to defaults');
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Palette className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Portal Customization
          </h1>
          <p className="text-gray-600">Customize the look and feel of your portal</p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="h-4 w-4 mr-2" />
            {previewMode ? 'Edit Mode' : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={handleSave}
          >
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Customization Settings */}
        <div className="space-y-6">
          {/* Branding */}
          <Card>
            <CardHeader>
              <CardTitle>Branding</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Portal Name</label>
                <Input
                  value={customization.portalName}
                  onChange={(e) => setCustomization({...customization, portalName: e.target.value})}
                  placeholder="Enter portal name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Logo Upload</label>
                <div className="flex items-center space-x-3">
                  <img src={customization.logo} alt="Logo" className="h-12 w-auto" />
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload New Logo
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Welcome Message</label>
                <Textarea
                  value={customization.welcomeMessage}
                  onChange={(e) => setCustomization({...customization, welcomeMessage: e.target.value})}
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Footer Text</label>
                <Input
                  value={customization.footerText}
                  onChange={(e) => setCustomization({...customization, footerText: e.target.value})}
                />
              </div>
            </CardContent>
          </Card>

          {/* Theme Colors */}
          <Card>
            <CardHeader>
              <CardTitle>Theme Colors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Primary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customization.primaryColor}
                      onChange={(e) => setCustomization({...customization, primaryColor: e.target.value})}
                      className="h-10 w-10 rounded cursor-pointer"
                    />
                    <Input
                      value={customization.primaryColor}
                      onChange={(e) => setCustomization({...customization, primaryColor: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Secondary Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customization.secondaryColor}
                      onChange={(e) => setCustomization({...customization, secondaryColor: e.target.value})}
                      className="h-10 w-10 rounded cursor-pointer"
                    />
                    <Input
                      value={customization.secondaryColor}
                      onChange={(e) => setCustomization({...customization, secondaryColor: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Accent Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customization.accentColor}
                      onChange={(e) => setCustomization({...customization, accentColor: e.target.value})}
                      className="h-10 w-10 rounded cursor-pointer"
                    />
                    <Input
                      value={customization.accentColor}
                      onChange={(e) => setCustomization({...customization, accentColor: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature Toggles */}
          <Card>
            <CardHeader>
              <CardTitle>Features</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Enable Notifications</label>
                <input
                  type="checkbox"
                  checked={customization.enableNotifications}
                  onChange={(e) => setCustomization({...customization, enableNotifications: e.target.checked})}
                  className="h-5 w-5 rounded"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Enable AI Chat</label>
                <input
                  type="checkbox"
                  checked={customization.enableChat}
                  onChange={(e) => setCustomization({...customization, enableChat: e.target.checked})}
                  className="h-5 w-5 rounded"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Enable Rewards System</label>
                <input
                  type="checkbox"
                  checked={customization.enableRewards}
                  onChange={(e) => setCustomization({...customization, enableRewards: e.target.checked})}
                  className="h-5 w-5 rounded"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <Card className="border-2 border-[#D4AF37]">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Eye className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Preview of portal with custom settings */}
              <div className="border-2 rounded-lg overflow-hidden">
                {/* Header Preview */}
                <div 
                  className="p-4 text-white flex items-center justify-between"
                  style={{ backgroundColor: customization.primaryColor }}
                >
                  <div className="flex items-center space-x-3">
                    <img src={customization.logo} alt="Logo" className="h-8 w-auto" />
                    <span className="font-bold">{customization.portalName}</span>
                  </div>
                  <div className="text-xs opacity-80">Preview</div>
                </div>

                {/* Welcome Section Preview */}
                <div className="p-6 bg-gray-50">
                  <h3 className="text-xl font-bold mb-2" style={{ color: customization.primaryColor }}>
                    Welcome!
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{customization.welcomeMessage}</p>
                  
                  {/* Sample Button */}
                  <Button 
                    size="sm"
                    className="text-white"
                    style={{ backgroundColor: customization.secondaryColor }}
                  >
                    Sample Button
                  </Button>
                </div>

                {/* Footer Preview */}
                <div 
                  className="p-3 text-xs text-center text-white"
                  style={{ backgroundColor: customization.accentColor }}
                >
                  {customization.footerText}
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Changes will apply to all portal users after saving.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PortalCustomization;